import { useRef, useState, useEffect } from "preact/hooks"
import { getInGridCtaInlineStyle } from "@/utils/ingridCtaInlineStyle"
import { InGridVideoConfig } from "@/utils/ingridConfig"
import style from "./InGridVideoTile.module.css"

type Props = {
  config: InGridVideoConfig
}

export default function InGridVideoTile({ config }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const tileRef = useRef<HTMLDivElement>(null)
  const userPausedRef = useRef(false)
  const [paused, setPaused] = useState(false)

  const [verticalPosition = "bottom", horizontalPosition = "left"] = config.contentPosition.split("-")
  const oppositeVertical = verticalPosition === "top" ? "bottom" : verticalPosition === "bottom" ? "top" : "top"
  const oppositeHorizontal = horizontalPosition === "left" ? "right" : horizontalPosition === "right" ? "left" : "right"
  const controlPosition = `${oppositeVertical}-${oppositeHorizontal}`

  useEffect(() => {
    const video = videoRef.current
    const tile = tileRef.current
    if (!video || !tile) return

    video.play().catch(() => {
      userPausedRef.current = true
      setPaused(true)
    })

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          video.pause()
        } else if (!userPausedRef.current) {
          video.play().catch(() => undefined)
        }
      },
      { threshold: 0.25 }
    )
    observer.observe(tile)

    return () => {
      observer.disconnect()
    }
  }, [])

  const togglePlayback = () => {
    const video = videoRef.current
    if (!video) return
    if (video.paused) {
      userPausedRef.current = false
      setPaused(false)
      video.play().catch(() => undefined)
    } else {
      userPausedRef.current = true
      setPaused(true)
      video.pause()
    }
  }

  const videoElement = config.videoUrl ? (
    <video
      ref={videoRef}
      src={config.videoUrl}
      autoPlay
      muted
      loop
      playsInline
      aria-label={config.altText}
      className={`ns-ingrid-video ${style.video}`}
    />
  ) : null

  const hasContent = config.eyebrow || config.headline || config.copy || config.ctaText

  return (
    <div
      ref={tileRef}
      className={`ns-ingrid-tile ns-ingrid-video-tile ${style.tile}`}
      data-content-position={config.contentPosition}
      data-desktop-width={config.desktopWidth}
      data-desktop-height={config.desktopHeight}
      data-mobile-width={config.mobileWidth}
      data-mobile-height={config.mobileHeight}
      data-text-color={config.textColor}
      data-eyebrow-color={config.eyebrowColor}
      data-headline-color={config.headlineColor}
      data-copy-color={config.copyColor}
      data-cta-color={config.ctaColor}
      data-cta-primary-text-color={config.ctaPrimaryTextColor}
    >
      {videoElement}
      {config.videoUrl && (
        <button
          type="button"
          className={`ns-ingrid-playback-toggle ${style.playbackToggle}`}
          data-control-position={controlPosition}
          onClick={togglePlayback}
          aria-label={paused ? "Play video" : "Pause video"}
        >
          <span className={`ns-ingrid-playback-icon ${style.playbackIcon}`} data-paused={paused} />
        </button>
      )}
      {hasContent && (
        <div className={`ns-ingrid-content ${style.content}`}>
          {config.eyebrow && (
            <span
              className="ns-ingrid-eyebrow"
              style={config.eyebrowColor ? { color: config.eyebrowColor } : undefined}
            >
              {config.eyebrow}
            </span>
          )}
          {config.headline && (
            <h2
              className="ns-ingrid-headline"
              style={config.headlineColor ? { color: config.headlineColor } : undefined}
            >
              {config.headline}
            </h2>
          )}
          {config.copy && (
            <p className="ns-ingrid-copy" style={config.copyColor ? { color: `${config.copyColor}` } : undefined}>
              {config.copy}
            </p>
          )}
          {config.ctaText && config.ctaLink && (
            <a
              href={config.ctaLink}
              className="ns-ingrid-cta"
              data-cta-style={config.ctaStyle}
              style={getInGridCtaInlineStyle(config.ctaStyle, config.ctaColor, config.ctaPrimaryTextColor)}
            >
              {config.ctaText}
            </a>
          )}
        </div>
      )}
    </div>
  )
}
